import React, { useState, useEffect } from 'react';
import { Ruler } from 'lucide-react';
import { SEO } from '../components/SEO';

interface BraSizeState {
  underBustMeasurement: string;
  bustMeasurement: string;
  measurementSystem: 'cm' | 'inch';
  region: 'eu' | 'us' | 'uk';
}

interface BraSizeResult {
  bandSize: string;
  cupSize: string;
  fullSize: string;
  sisterSizes: string[];
}

function KalkulatorGrudnjaka() {
  const [values, setValues] = useState<BraSizeState>({
    underBustMeasurement: '',
    bustMeasurement: '',
    measurementSystem: 'cm',
    region: 'eu'
  });

  const [results, setResults] = useState<BraSizeResult | null>(null);

  // Cup size arrays for different regions
  const cupSizes = {
    eu: ['AA', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'],
    us: ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD/F', 'G', 'H', 'I', 'J', 'K'],
    uk: ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG', 'H']
  };

  const calculateBraSize = () => {
    const underBust = parseFloat(values.underBustMeasurement);
    const bust = parseFloat(values.bustMeasurement);
    
    if (isNaN(underBust) || isNaN(bust) || underBust <= 0 || bust <= 0) {
      return null;
    }

    // Convert to cm if needed
    const underBustCm = values.measurementSystem === 'inch' ? underBust * 2.54 : underBust;
    const bustCm = values.measurementSystem === 'inch' ? bust * 2.54 : bust;

    // Calculate band size
    let bandSize: number;
    if (values.region === 'eu') {
      // EU sizing: round underbust to nearest 5
      bandSize = Math.round(underBustCm / 5) * 5;
    } else if (values.region === 'us') {
      // US sizing: underbust in inches + 4 if even, +5 if odd
      const underBustInch = underBustCm / 2.54;
      bandSize = Math.round(underBustInch) + (Math.round(underBustInch) % 2 === 0 ? 4 : 5);
    } else { // UK
      // UK sizing: similar to US
      const underBustInch = underBustCm / 2.54;
      bandSize = Math.round(underBustInch) + (Math.round(underBustInch) % 2 === 0 ? 4 : 5);
    }

    // Calculate cup size
    const difference = bustCm - underBustCm;
    let cupIndex = Math.max(0, Math.floor(difference / 2.5) - 1);
    
    // Ensure cup index is within bounds
    cupIndex = Math.min(cupIndex, cupSizes[values.region].length - 1);
    
    const cupSize = cupSizes[values.region][cupIndex];
    const fullSize = `${bandSize}${cupSize}`;

    // Calculate sister sizes (one band up and down)
    const sisterSizes = [];
    
    // One band size down, one cup size up
    if (cupIndex + 1 < cupSizes[values.region].length) {
      sisterSizes.push(`${bandSize - (values.region === 'eu' ? 5 : 2)}${cupSizes[values.region][cupIndex + 1]}`);
    }
    
    // One band size up, one cup size down
    if (cupIndex > 0) {
      sisterSizes.push(`${bandSize + (values.region === 'eu' ? 5 : 2)}${cupSizes[values.region][cupIndex - 1]}`);
    }

    return {
      bandSize: bandSize.toString(),
      cupSize,
      fullSize,
      sisterSizes
    };
  };

  const handleInputChange = (field: keyof BraSizeState, value: string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.underBustMeasurement && values.bustMeasurement) {
      const results = calculateBraSize();
      setResults(results);
    }
  }, [values]);

  const getFitTips = () => {
    if (!results) return [];

    const tips = [
      "Grudnjak bi trebao udobno pristajati, bez utiskivanja ili podizanja.",
      "Traka grudnjaka treba biti vodoravna i ne bi se trebala podizati na leđima.",
      "Košarice bi trebale u potpunosti obuhvatiti grudi bez prelijevanja ili nabora.",
      "Središnji dio grudnjaka (most) trebao bi ravno ležati na prsnom košu.",
      "Naramenice ne bi trebale urezivati se u ramena niti kliziti."
    ];

    return tips;
  };

  const getCommonIssues = () => {
    return {
      "Traka se podiže na leđima": "Veličina trake je prevelika ili su košarice premale.",
      "Košarice se nabiru": "Košarice su prevelike ili je grudnjak star i istegnut.",
      "Grudi se prelijevaju iz košarica": "Košarice su premale, potrebna je veća veličina.",
      "Naramenice se urezuju": "Grudnjak ne pruža dovoljno potpore ili su naramenice pretijesne.",
      "Središnji dio ne leži ravno": "Košarice su premale ili je traka prevelika."
    };
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Grudnjaka",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte svoju idealnu veličinu grudnjaka. Precizni izračuni za EU, UK i US veličine s korisnim savjetima za savršeno pristajanje.",
    "url": "https://online-kalkulator.com/kalkulator-grudnjaka",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun veličine grudnjaka",
      "Podrška za EU, UK i US veličine",
      "Izračun sestrinskih veličina",
      "Savjeti za savršeno pristajanje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Grudnjaka | Izračun Veličine Grudnjaka"
        description="Izračunajte svoju idealnu veličinu grudnjaka. Precizni izračuni za EU, UK i US veličine s korisnim savjetima za savršeno pristajanje."
        canonicalUrl="/kalkulator-grudnjaka"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Ruler className="w-8 h-8 text-pink-500" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Veličine Grudnjaka</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Measurement System Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mjerni sustav:
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="cm"
                      checked={values.measurementSystem === 'cm'}
                      onChange={() => handleInputChange('measurementSystem', 'cm')}
                      className="h-4 w-4 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="ml-2 text-gray-700">Centimetri (cm)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="inch"
                      checked={values.measurementSystem === 'inch'}
                      onChange={() => handleInputChange('measurementSystem', 'inch')}
                      className="h-4 w-4 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="ml-2 text-gray-700">Inči (in)</span>
                  </label>
                </div>
              </div>

              {/* Region Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Standard veličina:
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="eu"
                      checked={values.region === 'eu'}
                      onChange={() => handleInputChange('region', 'eu')}
                      className="h-4 w-4 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="ml-2 text-gray-700">Europski (EU)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="us"
                      checked={values.region === 'us'}
                      onChange={() => handleInputChange('region', 'us')}
                      className="h-4 w-4 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="ml-2 text-gray-700">Američki (US)</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="uk"
                      checked={values.region === 'uk'}
                      onChange={() => handleInputChange('region', 'uk')}
                      className="h-4 w-4 text-pink-500 focus:ring-pink-400"
                    />
                    <span className="ml-2 text-gray-700">Britanski (UK)</span>
                  </label>
                </div>
              </div>

              {/* Measurements */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg ispod grudi
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.underBustMeasurement}
                      onChange={(e) => handleInputChange('underBustMeasurement', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                      placeholder={`npr. ${values.measurementSystem === 'cm' ? '75' : '30'}`}
                      step="0.1"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {values.measurementSystem}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opseg grudi
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.bustMeasurement}
                      onChange={(e) => handleInputChange('bustMeasurement', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-400 focus:border-transparent"
                      placeholder={`npr. ${values.measurementSystem === 'cm' ? '90' : '36'}`}
                      step="0.1"
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {values.measurementSystem}
                    </span>
                  </div>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="text-center py-3 px-4 rounded-lg bg-pink-50">
                    <p className="text-sm text-gray-600 mb-1">Vaša veličina grudnjaka:</p>
                    <p className="text-2xl font-bold text-pink-600">{results.fullSize}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      Opseg: {results.bandSize} | Košarica: {results.cupSize}
                    </p>
                  </div>

                  {results.sisterSizes.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Sestrinske veličine:</h3>
                      <div className="flex justify-center space-x-4">
                        {results.sisterSizes.map((size, index) => (
                          <span key={index} className="px-3 py-1 bg-gray-100 rounded-full text-gray-700">
                            {size}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Sestrinske veličine mogu pružiti sličan fit s različitim kombinacijama opsega i košarice.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Pravilno Izmjeriti</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opseg ispod grudi</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Postavite mjernu vrpcu izravno ispod grudi</li>
                      <li>Vrpca treba biti vodoravna i čvrsto priljubljena uz tijelo</li>
                      <li>Izdahnite prije očitanja mjere</li>
                      <li>Zaokružite na najbliži cijeli broj</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Opseg grudi</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Postavite mjernu vrpcu preko najistaknutijeg dijela grudi</li>
                      <li>Vrpca treba biti vodoravna i ne previše zategnuta</li>
                      <li>Mjerenje obavite u opuštenom stojećem položaju</li>
                      <li>Ako već nosite grudnjak, skinite ga za preciznije mjerenje</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Savršeno Pristajanje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  {getFitTips().map((tip, index) => (
                    <li key={index}>{tip}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Česti Problemi i Rješenja</h2>
                <div className="space-y-3">
                  {Object.entries(getCommonIssues()).map(([issue, solution], index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <p className="font-medium text-gray-700">{issue}</p>
                      <p className="text-gray-600 text-sm mt-1">{solution}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Razlike u Standardima Veličina</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Europski (EU) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Veličine trake: 65, 70, 75, 80, 85, 90, 95, 100, 105, 110</li>
                      <li>Veličine košarica: AA, A, B, C, D, E, F, G, H, I, J, K</li>
                      <li>Mjere se izražavaju u centimetrima</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Američki (US) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Veličine trake: 30, 32, 34, 36, 38, 40, 42, 44, 46, 48</li>
                      <li>Veličine košarica: AA, A, B, C, D, DD, DDD/F, G, H, I, J, K</li>
                      <li>Mjere se izražavaju u inčima</li>
                      <li>Dodaje se 4" na paran opseg ispod grudi, 5" na neparan</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Britanski (UK) standard</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Veličine trake: 30, 32, 34, 36, 38, 40, 42, 44, 46, 48</li>
                      <li>Veličine košarica: AA, A, B, C, D, DD, E, F, FF, G, GG, H</li>
                      <li>Mjere se izražavaju u inčima</li>
                      <li>Sličan američkom, ali s drugačijim oznakama košarica</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kada Ponovno Izmjeriti</h2>
                <p className="text-gray-600 mb-4">
                  Preporučuje se ponovno mjerenje veličine grudnjaka u sljedećim situacijama:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Nakon značajne promjene težine (više od 3-5 kg)</li>
                  <li>Tijekom i nakon trudnoće</li>
                  <li>Tijekom hormonalnih promjena (menopauza)</li>
                  <li>Nakon operacije grudi</li>
                  <li>Ako primijetite da vaš trenutni grudnjak ne pristaje dobro</li>
                  <li>Svakih 6-12 mjeseci kao redovita provjera</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Veličina grudnjaka može varirati ovisno o proizvođaču</li>
                  <li>Različiti stilovi grudnjaka mogu zahtijevati različite veličine</li>
                  <li>Ovaj kalkulator daje okvirnu procjenu, a ne apsolutnu veličinu</li>
                  <li>Najbolje je isprobati grudnjak prije kupnje kad god je to moguće</li>
                  <li>Profesionalno mjerenje u specijaliziranoj trgovini može pružiti najpreciznije rezultate</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorGrudnjaka;